// loadEnvironmentNode — the pure half of the `loadEnvironment` event-graph node
// (ENG-1031): given the org environment rows and what the node selected, say
// WHICH row to hand to applyEnvironment, or WHY there is none.
//
// It lives outside EventRuntime.js for the usual reason in this repo: that
// module transitively imports the asset/API layer through extensionless
// specifiers only Vite resolves, so it cannot be loaded under plain
// `node --test`. Same precedent as eventStarters.js / graphGroup.js /
// onConstructDispatch.js.
//
// Two rules it exists to enforce:
//   - A selection that resolves to nothing is an ERROR with a reason, never a
//     plausible substitute (no "first environment in the list" fallback). The
//     caller reports it; the scene keeps whatever it had.
//   - The URL is read off the row the server published, in every shape the row
//     arrives in. Nothing here computes or parses a storage path.

/** The splat URL a row states, across the row shapes this app sees. */
export function environmentRowUrl(row) {
  if (!row) return null
  return (
    row.url ??
    row.fileUrl ??
    row.file_url ??
    row.raw?.fileUrl ??
    row.raw?.file_url ??
    null
  )
}

/** The display name a row states (falling back to its id, never to a guess). */
export function environmentRowLabel(row) {
  if (!row) return ''
  return row.name ?? row.raw?.name ?? row.id ?? ''
}

function sameUrl(a, b) {
  if (!a || !b) return false
  return String(a).split('?')[0] === String(b).split('?')[0]
}

/**
 * Is `row` already the active environment? Used to make a re-fired
 * `onConstruct` (it fires on every applied editor edit) a no-op instead of
 * re-applying the same splat, pose and shared tune each time.
 */
export function isAlreadyActive(row, activeSplatUrl) {
  return sameUrl(environmentRowUrl(row), activeSplatUrl)
}

/**
 * Which environment row the node means.
 *
 * `wired` is the value of the node's `in-environment` data pin when something is
 * wired to it — an id or a name — and it wins over the dropdown, matching the
 * PlayBodyClip / PlayPrefab convention ("wired overrides dropdown").
 *
 * Returns `{ none: true }` for an empty selection, `{ row }` on a hit, or
 * `{ error, detail }` where error is one of:
 *   'no-environments' — the org has no environment assets at all
 *   'not-found'       — a selection that matches no row (deleted, or a typo in
 *                       a wired string). `detail` is what was asked for.
 *
 * NONE IS A CHOICE, NOT A MISTAKE (Isaac, 2026-09-01: "none by default"). An
 * empty selection used to be the `no-selection` error, which meant a character
 * could only boot without an environment by having no node at all — so the way
 * to stop loading a 568 MB splat was to find and delete a node in the event
 * graph, which is exactly the thing nobody could find. It now resolves to a
 * deliberate "load nothing", and the caller unloads whatever a BLUEPRINT
 * previously loaded (never the user's own pick — see unloadAuthoredEnvironment).
 *
 * It is answered BEFORE the empty-org check on purpose: "load nothing" is a
 * complete answer with no catalogue, and returning 'no-environments' there sent
 * a node that wants nothing into EventRuntime's 15 s wait-for-the-list.
 */
export function resolveEnvironmentSelection(rows, { environmentId, environmentName, wired } = {}) {
  const list = Array.isArray(rows) ? rows : []

  const asked = firstNonEmpty(wired, environmentId, environmentName)
  if (!asked) return { none: true }

  if (list.length === 0) return { error: 'no-environments', detail: null }

  // id first — it is the stable server identity. Name only after, because two
  // uploads can share a name and a rename must not silently repoint the node.
  const byId = list.find(r => r?.id && String(r.id) === asked)
  if (byId) return { row: byId }

  const byName = list.find(r => environmentRowLabel(r) === asked)
  if (byName) return { row: byName }

  return { error: 'not-found', detail: asked }
}

function firstNonEmpty(...vals) {
  for (const v of vals) {
    if (v === null || v === undefined) continue
    const s = String(v).trim()
    if (s) return s
  }
  return ''
}

/**
 * Normalise the node's `format` data field to a bare extension token.
 *
 *   '' | null | undefined → ''  (default: follow the row's current splat)
 *   'ply' | '.PLY' | 'SPZ' → 'ply' | 'ply' | 'spz'
 *
 * The token indexes the server's format-keyed files map ('ply' → 'splat_ply').
 */
export function normalizeEnvironmentFormat(format) {
  if (format === null || format === undefined) return ''
  return String(format).trim().toLowerCase().replace(/^\./, '')
}

/**
 * The splat-format tokens the server published for one environment, derived from
 * its `files` map ('splat_ply' → 'ply'). Sorted, so the dropdown order is
 * stable. Used by the node UI to enable exactly the variants that exist and
 * disable the rest with a reason (ENG-1070) — never a silent fallback.
 */
export function availableEnvironmentFormats(files) {
  if (!files || typeof files !== 'object') return []
  return Object.keys(files)
    .filter(k => k.startsWith('splat_'))
    .map(k => k.slice('splat_'.length))
    .filter(Boolean)
    .sort()
}

/**
 * Which splat URL a resolved environment row should load, honouring the node's
 * `format` field.
 *
 * `files` is the server's logical-name → URL map for this environment (from
 * environmentFiles()), or null when it has not been / could not be fetched.
 *
 *   format ''  → the row's own splat URL, verbatim. Unchanged behaviour, and it
 *                needs no files map (default path never blocks on a fetch).
 *   format fmt → files['splat_' + fmt], read VERBATIM. Never derived from the
 *                row URL by rewriting the extension (the ENG-999 forbidden
 *                pattern). Absent from the map → { error:'format-unavailable' },
 *                never a silent fallback to the current splat.
 *
 * Returns { url } on a hit, or { error, detail }.
 */
export function resolveEnvironmentFormatUrl(row, format, files) {
  const fmt = normalizeEnvironmentFormat(format)
  if (!fmt) {
    const url = environmentRowUrl(row)
    return url ? { url } : { error: 'no-url', detail: null }
  }
  const url = files?.[`splat_${fmt}`] ?? null
  if (!url) return { error: 'format-unavailable', detail: fmt }
  return { url }
}

/**
 * One sentence saying what went wrong, for the console warning and the
 * viewport message. Kept here so the runtime and any UI describing the same
 * failure cannot drift into two different wordings.
 */
export function describeEnvironmentError(error, detail) {
  if (error === 'no-environments') {
    return 'no environments in this org — drop a .ply/.splat onto the content browser to upload one'
  }
  if (error === 'not-found') {
    return `environment "${detail}" not found — it may have been deleted or renamed`
  }
  if (error === 'format-unavailable') {
    const fmt = detail ? String(detail).toUpperCase() : 'that format'
    return `this environment has no ${fmt} variant — the server lists only the splat formats present in its folder`
  }
  if (error === 'no-url') {
    return 'environment row has no splat URL to load'
  }
  return `environment could not be resolved (${error})`
}

/**
 * What to say when the FORMAT lookup itself failed — the server never told us
 * which splat encodings this environment has, so every non-default option is
 * disabled and the node has to explain WHY.
 *
 * Split from `describeEnvironmentError` because these are not authoring
 * mistakes: the selection is fine and the ask failed. Returns
 * `{ suffix, detail }` — a few words for each disabled option, and one sentence
 * under the dropdown.
 *
 * The distinction the two carry is the point. A single "(couldn't read
 * formats)" for every failure sent the 2026-08-31 report hunting for missing
 * baked files while the server was answering 200 for all of them; an auth
 * refusal, a server fault and an unreachable server need three different next
 * moves, and only the status tells them apart. `ApiError`/`UnauthorizedError`
 * both carry `.status` (src/lib/errors.js); a fetch that threw carries none,
 * which is itself the signal that nothing answered.
 */
export function describeFormatLookupError(err) {
  const status = err?.status ?? null
  const msg = err?.message ? String(err.message) : 'unknown error'
  if (status === 401 || status === 403) {
    return {
      suffix: ' (server refused)',
      detail: `the server refused to list this environment's files (HTTP ${String(status)}) — a sign-in or org-access problem, not a missing file`,
    }
  }
  if (status === 404) {
    return {
      suffix: ' (not on the server)',
      detail: 'the server has no environment with this id (HTTP 404) — it may have been deleted',
    }
  }
  if (typeof status === 'number' && status >= 500) {
    return {
      suffix: ' (server error)',
      detail: `the server failed while listing this environment's files (HTTP ${String(status)}) — its storage may be unreachable; retry, and report the status if it persists`,
    }
  }
  if (typeof status === 'number') {
    return { suffix: ' (server error)', detail: `the server answered HTTP ${String(status)} when asked for this environment's files` }
  }
  return {
    suffix: ' (no answer)',
    detail: `could not reach the server to list this environment's files (${msg}) — check the console for the failing request`,
  }
}
